import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DocumentScanner from '../components/DocumentScanner';
import api from '../utils/api';
import { BRAND } from '../brand';
import { BIOMETRIC_CONSENT_VERSION, BiometricConsentBlock } from '../legal/biometricConsent';

const RegisterNew = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const campaignCode = searchParams.get('campaign') || searchParams.get('promo') || '';
  const [referrerName, setReferrerName] = useState('');
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [referralPromo, setReferralPromo] = useState(null);
  
  // Validate referral code on mount
  useEffect(() => {
    if (referralCode) {
      api.get(`/gb/validate-referral/${referralCode}`).then(res => {
        if (res.data.valid) setReferrerName(res.data.referrer_name);
      }).catch(() => {});
      // También cargar la promo de referidos (si está activa muestra +pts)
      api.get('/gb/referral-promo').then(res => {
        if (res.data?.active) setReferralPromo(res.data);
      }).catch(() => {});
    }
    if (campaignCode) {
      api.get(`/campaigns/validate/${campaignCode}`).then(res => {
        if (res.data.valid) setCampaignInfo(res.data);
      }).catch(() => {});
    }
  }, [referralCode, campaignCode]);
  
  // Paso actual del registro
  const [step, setStep] = useState(1);
  // 1: Datos básicos
  // 2: Scan documento frente
  // 3: Scan documento reverso
  // 4: Confirmar datos extraídos
  // 5: Datos UAF (dirección, ocupación, PEP)
  // 6: Selfie con documento
  // 7: Procesando / Resultado

  // Estados de formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    whatsapp: '',
    countryCode: '+56' // Chile por defecto
  });

  // Opciones de país para WhatsApp
  const countryOptions = [
    { code: '+56', flagCode: 'cl', name: 'Chile' },
    { code: '+591', flagCode: 'bo', name: 'Bolivia' },
    { code: '+54', flagCode: 'ar', name: 'Argentina' },
    { code: '+51', flagCode: 'pe', name: 'Perú' },
    { code: '+58', flagCode: 've', name: 'Venezuela' },
    { code: '+57', flagCode: 'co', name: 'Colombia' },
    { code: '+55', flagCode: 'br', name: 'Brasil' },
    { code: '+593', flagCode: 'ec', name: 'Ecuador' },
    { code: '+595', flagCode: 'py', name: 'Paraguay' },
    { code: '+598', flagCode: 'uy', name: 'Uruguay' }
  ];
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const selectedCountry = countryOptions.find(c => c.code === formData.countryCode) || countryOptions[0];

  // Documentos capturados
  const [documentFront, setDocumentFront] = useState(null);
  const [documentFrontUrl, setDocumentFrontUrl] = useState(null);
  const [documentBack, setDocumentBack] = useState(null);
  const [documentBackUrl, setDocumentBackUrl] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [selfieUrl, setSelfieUrl] = useState(null);

  // Datos extraídos por IA
  const [extractedData, setExtractedData] = useState({
    full_name: '',
    rut_passport: '',
    nationality: '',
    birth_date: '',
    document_expiry: '',
    document_type: ''
  });
  const [extractionLoading, setExtractionLoading] = useState(false);

  // Datos UAF
  const [uafData, setUafData] = useState({
    address: '',
    city: '',
    occupation: '',
    source_of_funds: '',
    is_pep: false
  });

  // Estados de UI
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState('document');
  const [scannerTitle, setScannerTitle] = useState('');
  const [scannerInstructions, setScannerInstructions] = useState('');

  // Email OTP verification
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // Aceptar términos y condiciones
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Nota: la versión del consentimiento y el bloque UI viven en un módulo
  // legal único (`../legal/biometricConsent`). No redeclarar aquí.
  const [biometricConsent, setBiometricConsent] = useState(false);

  // Verificación resultado
  const [verificationResult, setVerificationResult] = useState(null);

  // Validar paso 1
  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.full_name || !formData.whatsapp) {
      setError('Todos los campos son obligatorios');
      return false;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Ingresa un email válido');
      return false;
    }
    return true;
  };

  // Avanzar al scan de documento
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateStep1()) return;

    // If already verified email, go to step 2
    if (emailVerified) {
      setStep(2);
      openScanner('document-front');
      return;
    }

    // Send OTP to email
    setOtpLoading(true);
    try {
      const resp = await api.post('/auth/send-verification-code', { 
        email: formData.email.trim().toLowerCase() 
      });
      // BYPASS: si el backend desactivó la verificación, saltamos paso del código
      if (resp.data?.bypass) {
        setEmailVerified(true);
        setOtpSent(false);
        setError('');
        setStep(2);
        openScanner('document-front');
        return;
      }
      setOtpSent(true);
      setOtpCountdown(60);
      setError('');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'EMAIL_ALREADY_REGISTERED') {
        setError('Ya existe una cuenta con este correo. Intenta iniciar sesión.');
      } else if (err.response?.status === 429) {
        setError('Demasiados intentos. Espera unos minutos.');
      } else {
        setError(detail || 'Error al enviar el código. Verifica tu correo.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP code — y crear cuenta inmediatamente con datos mínimos
  // (registro parcial pre-KYC). Ver /app/memory/CHANGELOG.md 2026-07-23.
  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }
    setOtpLoading(true);
    setError('');
    try {
      await api.post('/auth/verify-email-code', {
        email: formData.email.trim().toLowerCase(),
        code: otpCode
      });
      setEmailVerified(true);
      setOtpSent(false);
      // === Registro parcial: crear la cuenta ya, con lo mínimo.
      // Desde ahora el usuario tiene JWT y puede llamar /kyc/analyze-document.
      // El rut_passport queda vacío y kyc_status='incomplete_registration'
      // hasta que complete /kyc/complete-verification al final del flujo.
      try {
        const registerData = {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          full_name: formData.full_name,
          whatsapp: `${formData.countryCode}${formData.whatsapp}`,
          referral_code: referralCode || undefined,
          campaign_code: campaignCode || undefined,
        };
        const registerResponse = await api.post('/auth/register', registerData);
        const { access_token } = registerResponse.data || {};
        if (access_token) {
          localStorage.setItem('token', access_token);
        }
      } catch (regErr) {
        const detail = regErr.response?.data?.detail || '';
        if (detail === 'EMAIL_ALREADY_REGISTERED') {
          // Retomar: probablemente registro parcial anterior. Intentar login
          // silencioso con el password que acaba de ingresar en este mismo flujo.
          try {
            await login(formData.email.trim().toLowerCase(), formData.password);
          } catch (loginErr) {
            setError('Ya existe una cuenta con este correo pero la contraseña no coincide. Ingresa tu contraseña original o recupérala.');
            return;
          }
        } else if (detail === 'WHATSAPP_ALREADY_REGISTERED') {
          setError('Ya existe una cuenta con este número de WhatsApp. Inicia sesión con tu correo.');
          return;
        } else {
          setError(detail || 'No pudimos crear tu cuenta. Intenta de nuevo.');
          return;
        }
      }
      // Proceed to step 2
      setStep(2);
      openScanner('document-front');
    } catch (err) {
      setError(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setOtpLoading(true);
    setError('');
    try {
      await api.post('/auth/send-verification-code', {
        email: formData.email.trim().toLowerCase()
      });
      setOtpCountdown(60);
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reenviar');
    } finally {
      setOtpLoading(false);
    }
  };

  // Abrir scanner según tipo
  const openScanner = (type) => {
    switch(type) {
      case 'document-front':
        setScannerMode('document');
        setScannerTitle('Foto de tu documento');
        setScannerInstructions('Saca una foto del FRENTE de tu carnet o pasaporte');
        break;
      case 'document-back':
        setScannerMode('document');
        setScannerTitle('Ahora el reverso');
        setScannerInstructions('Da vuelta tu documento y saca una foto del reverso');
        break;
      case 'selfie':
        setScannerMode('selfie');
        setScannerTitle('Selfie de Verificación');
        setScannerInstructions('Mira de frente a la cámara con buena iluminación');
        break;
      default:
        break;
    }
    setShowScanner(true);
  };

  // Manejar captura de documento frente
  const handleDocumentFrontCapture = async (file, url) => {
    setShowScanner(false);
    setDocumentFront(file);
    setDocumentFrontUrl(url);
    setStep(3);
    // Abrir scanner para reverso
    setTimeout(() => openScanner('document-back'), 500);
  };

  // Manejar captura de documento reverso
  const handleDocumentBackCapture = async (file, url) => {
    setShowScanner(false);
    setDocumentBack(file);
    setDocumentBackUrl(url);
    setStep(4);
    // Analizar documentos con IA
    await analyzeDocuments(documentFront, file);
  };

  // Analizar documentos con IA — usa /kyc/analyze-document (JWT + rate-limit).
  // La sesión ya existe porque creamos la cuenta después del OTP (registro
  // parcial pre-KYC). El interceptor de `api` adjunta el Bearer token desde
  // localStorage.
  const analyzeDocuments = async (frontFile, backFile) => {
    setExtractionLoading(true);
    try {
      const analyzeData = new FormData();
      analyzeData.append('document_front', frontFile);
      if (backFile) analyzeData.append('document_back', backFile);
      const response = await api.post('/kyc/analyze-document', analyzeData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data || {};
      setExtractedData({
        full_name: data.full_name || '',
        rut_passport: data.rut_passport || '',
        nationality: data.nationality || '',
        birth_date: data.birth_date || '',
        document_expiry: data.document_expiry || '',
        document_type: data.document_type || '',
      });
    } catch (err) {
      // OCR falló → dejar campos vacíos y avisar. El usuario los llena a mano.
      console.warn('analyze-document falló:', err?.response?.data || err?.message);
      setExtractedData({
        full_name: '',
        rut_passport: '',
        nationality: '',
        birth_date: '',
        document_expiry: '',
        document_type: '',
      });
      setError('No pudimos leer los datos del documento automáticamente. Complétalos a mano por favor.');
    } finally {
      setExtractionLoading(false);
    }
  };

  // Confirmar datos extraídos
  const handleConfirmExtractedData = () => {
    setError('');
    if (!extractedData.full_name || !extractedData.rut_passport) {
      setError('El nombre y RUT/ID son obligatorios');
      return;
    }
    setStep(5);
  };

  // Enviar datos UAF
  const handleUafSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!uafData.address || !uafData.city || !uafData.occupation || !uafData.source_of_funds) {
      setError('Todos los campos son obligatorios');
      return;
    }
    if (!acceptTerms) {
      setError('Debes aceptar los Términos y Condiciones para continuar');
      return;
    }
    if (!biometricConsent) {
      setError('Debes otorgar el consentimiento biométrico (Ley 21.719) antes de tomar la selfie.');
      return;
    }
    setStep(6);
    openScanner('selfie');
  };

  // Manejar captura de selfie
  const handleSelfieCapture = async (file, url) => {
    setShowScanner(false);
    setSelfieImage(file);
    setSelfieUrl(url);
    setStep(7);
    // Completar registro
    await completeRegistration(file);
  };

  // Completar registro con todos los datos.
  // La CUENTA ya existe (creada tras verificar OTP en handleVerifyOtp).
  // Aquí sólo subimos documentos + selfie a /kyc/complete-verification, que
  // saca al usuario de `kyc_status='incomplete_registration'`.
  const completeRegistration = async (selfieFile) => {
    setLoading(true);
    setError('');

    try {
      const kycFormData = new FormData();
      kycFormData.append('document_front', documentFront);
      if (documentBack) kycFormData.append('document_back', documentBack);
      kycFormData.append('selfie', selfieFile);

      kycFormData.append('extracted_full_name', extractedData.full_name);
      kycFormData.append('extracted_rut', extractedData.rut_passport);
      kycFormData.append('extracted_nationality', extractedData.nationality || '');
      kycFormData.append('extracted_birth_date', extractedData.birth_date || '');
      kycFormData.append('extracted_document_expiry', extractedData.document_expiry || '');

      kycFormData.append('address', uafData.address);
      kycFormData.append('city', uafData.city);
      kycFormData.append('occupation', uafData.occupation);
      kycFormData.append('source_of_funds', uafData.source_of_funds);
      kycFormData.append('is_pep', uafData.is_pep);
      kycFormData.append('biometric_consent', biometricConsent);
      kycFormData.append('biometric_consent_version', BIOMETRIC_CONSENT_VERSION);

      const kycResponse = await api.post('/kyc/complete-verification', kycFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setVerificationResult(kycResponse.data);

      // Refrescar la sesión para que el frontend vea el nuevo kyc_status.
      try {
        await login(formData.email.trim().toLowerCase(), formData.password);
      } catch (_) {
        // no-op: si falla el re-login, el usuario ya tiene un token válido.
      }

      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (err) {
      console.error('Error completando KYC:', err);
      const detail = err.response?.data?.detail || '';
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'No pudimos completar la verificación. Podés continuarla desde tu perfil.'
      );
      setVerificationResult({
        status: 'pending',
        message: 'Tu cuenta está creada pero falta completar la verificación. Puedes retomarla desde tu perfil.',
      });
      setTimeout(() => {
        navigate('/dashboard');
      }, 4000);
    } finally {
      setLoading(false);
    }
  };

  // Cancelar scanner
  const handleScannerCancel = () => {
    setShowScanner(false);
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 6) setStep(5);
  };

  // Ocupaciones disponibles
  const occupations = [
    'Empleado/a',
    'Independiente',
    'Empresario/a',
    'Comerciante',
    'Profesional',
    'Estudiante',
    'Jubilado/a',
    'Dueño/a de casa',
    'Otro'
  ];

  // Orígenes de fondos
  const sourcesOfFunds = [
    'Salario/Sueldo',
    'Negocio propio',
    'Ahorros personales',
    'Inversiones',
    'Venta de bienes',
    'Herencia',
    'Pensión/Jubilación',
    'Otro'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Scanner Modal */}
      {showScanner && (
        <DocumentScanner
          mode={scannerMode}
          title={scannerTitle}
          instructions={scannerInstructions}
          onCapture={
            step === 2 ? handleDocumentFrontCapture :
            step === 3 ? handleDocumentBackCapture :
            step === 6 ? handleSelfieCapture :
            () => {}
          }
          onCancel={handleScannerCancel}
        />
      )}

      {/* Header — fondo blanco, acentos con color de marca */}
      <div className="bg-white border-b border-slate-100 py-6 px-4">
        <div className="max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-2 mb-4">
            <img src="/logo.png" alt={`${BRAND.name}`} className="h-12" onError={(e) => e.target.style.display = 'none'} />
          </Link>
          <h1 className="text-xl font-bold text-slate-900">Crear Cuenta</h1>

          {/* Progress bar */}
          <div className="mt-4 flex items-center gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <div
                key={s}
                className="h-2 flex-1 rounded-full transition-all"
                style={{
                  backgroundColor: s <= step
                    ? (BRAND?.theme?.primary || '#8A0FBB')
                    : '#E5E7EB',
                }}
              />
            ))}
          </div>
          <p className="text-sm mt-2 text-slate-500">
            Paso {step} de 7: {
              step === 1 ? 'Tus datos' :
              step === 2 ? 'Foto de tu documento' :
              step === 3 ? 'Foto del reverso' :
              step === 4 ? 'Revisa tus datos' :
              step === 5 ? 'Casi listo' :
              step === 6 ? 'Una selfie y listo' :
              'Creando tu cuenta...'
            }
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto p-6">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Step 1: Datos básicos */}
        {step === 1 && (
          <form onSubmit={handleStep1Submit} className="space-y-4">
            {campaignInfo && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-2xl p-4 flex items-center gap-3" data-testid="campaign-banner">
                <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0 text-lg font-bold text-yellow-700">
                  $
                </div>
                <div>
                  <p className="font-bold text-yellow-800 text-sm">{campaignInfo.name}</p>
                  <p className="text-xs text-yellow-700">Descuento de ${campaignInfo.discount_clp?.toLocaleString()} CLP en tu primer envio de ${campaignInfo.min_transfer_clp?.toLocaleString()}+</p>
                </div>
              </div>
            )}
            {referrerName && (
              <div className={`rounded-2xl p-4 flex items-center gap-3 ${referralPromo ? 'bg-gradient-to-r from-pink-50 to-orange-50 border-2 border-pink-300' : 'bg-green-50 border border-green-200'}`} data-testid="referral-banner">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg ${referralPromo ? 'bg-pink-200' : 'bg-green-200'}`}>
                  <span role="img" aria-label="gift">&#127873;</span>
                </div>
                <div>
                  <p className={`font-bold text-sm ${referralPromo ? 'text-pink-800' : 'text-green-800'}`}>{referrerName} te invitó</p>
                  {referralPromo ? (
                    <p className="text-xs text-pink-700">🔥 PROMO: ambos ganan <strong>{referralPromo.bonus_points} pts (= $3.000 CLP)</strong> al hacer tu 1er envío de $100.000+</p>
                  ) : (
                    <p className="text-xs text-green-600">Ambos ganan 300 {BRAND.puntosProgramName} al completar tu primer envío de $100.000+</p>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Bienvenido a {BRAND.name}</h2>
              <p className="text-sm text-gray-500 mb-6">Crea tu cuenta en minutos y empieza a enviar dinero</p>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">
                    ¿Cómo te llamas?
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="Tu nombre completo"
                    required
                  />
                </div>

                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="tu@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">
                    WhatsApp
                  </label>
                  <div className="flex gap-2">
                    {/* Selector de país con bandera (custom dropdown) */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="flex items-center gap-2 border-2 border-gray-200 rounded-xl px-3 py-4 focus:border-green-500 focus:outline-none bg-white text-base font-medium transition-colors hover:border-gray-300"
                        style={{ minWidth: '120px' }}
                        data-testid="country-code-btn"
                      >
                        <img
                          src={`https://flagcdn.com/w40/${selectedCountry.flagCode}.png`}
                          alt={selectedCountry.name}
                          className="w-6 h-auto rounded-sm flex-shrink-0"
                        />
                        <span className="flex-1 text-left">{selectedCountry.code}</span>
                        <span className="text-gray-400 text-xs">▼</span>
                      </button>
                      {showCountryDropdown && (
                        <>
                          {/* Backdrop para cerrar */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowCountryDropdown(false)}
                          />
                          <div className="absolute top-full left-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-50 max-h-72 overflow-y-auto" style={{ minWidth: '200px' }}>
                            {countryOptions.map(country => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, countryCode: country.code });
                                  setShowCountryDropdown(false);
                                }}
                                className={`flex items-center gap-3 w-full px-3 py-2.5 hover:bg-green-50 transition text-left ${formData.countryCode === country.code ? 'bg-green-50' : ''}`}
                                data-testid={`country-opt-${country.flagCode}`}
                              >
                                <img
                                  src={`https://flagcdn.com/w40/${country.flagCode}.png`}
                                  alt={country.name}
                                  className="w-6 h-auto rounded-sm flex-shrink-0"
                                />
                                <span className="flex-1 text-sm font-medium text-gray-800">{country.name}</span>
                                <span className="text-xs text-gray-500">{country.code}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {/* Campo de número */}
                    <input
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => {
                        // Solo permitir números
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setFormData({...formData, whatsapp: value});
                      }}
                      className="flex-1 border-2 border-gray-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:outline-none transition-colors"
                      placeholder="9 1234 5678"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Selecciona tu pais en el menu de arriba
                  </p>
                </div>

                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">
                    Crea una contraseña para tu cuenta
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="Mínimo 6 caracteres"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-base font-bold text-gray-800 mb-2">
                    Repite tu contraseña
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-lg focus:border-green-500 focus:outline-none transition-colors"
                    placeholder="Repite tu contraseña"
                    required
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={otpLoading}
              className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-xl hover:bg-green-700 transition shadow-lg disabled:opacity-50"
              data-testid="register-step1-submit"
            >
              {otpLoading ? 'Enviando...' : 'Continuar →'}
            </button>

            {/* OTP Verification Section */}
            {otpSent && !emailVerified && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-200" data-testid="otp-section">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Revisa tu correo</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Te enviamos un codigo de 6 numeros a <strong className="text-green-700">{formData.email}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Revisa tambien en spam o correo no deseado</p>
                </div>

                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Código de 6 dígitos"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-center text-2xl font-bold tracking-widest focus:border-green-500 focus:outline-none mb-4"
                  data-testid="otp-input"
                />

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || otpCode.length !== 6}
                  className="w-full bg-green-600 text-white py-3.5 rounded-xl font-bold text-lg hover:bg-green-700 transition disabled:opacity-50 mb-3"
                  data-testid="verify-otp-btn"
                >
                  {otpLoading ? 'Verificando...' : 'Verificar Código'}
                </button>

                <div className="text-center">
                  {otpCountdown > 0 ? (
                    <p className="text-sm text-gray-500">Reenviar código en <strong>{otpCountdown}s</strong></p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpLoading}
                      className="text-green-600 font-bold text-sm"
                    >
                      Reenviar código
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => { setOtpSent(false); setOtpCode(''); setError(''); }}
                  className="w-full text-gray-400 text-sm py-2 mt-2"
                >
                  Cambiar correo electrónico
                </button>
              </div>
            )}

            <p className="text-center text-base text-gray-600">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-green-600 font-bold hover:underline">
                Inicia sesión
              </Link>
            </p>
          </form>
        )}

        {/* Step 4: Confirmar datos extraídos */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {extractionLoading ? 'Leyendo tu documento...' : 'Revisa que todo este bien'}
              </h2>

              {extractionLoading ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Nuestra IA esta leyendo los datos de tu documento</p>
                  <p className="text-xs text-gray-400 mt-1">Esto toma unos segundos</p>
                </div>
              ) : (
                <>
                  {/* Preview de documentos */}
                  <div className="flex gap-2 mb-4">
                    {documentFrontUrl && (
                      <img src={documentFrontUrl} alt="Frente" className="w-1/2 h-24 object-cover rounded-lg" />
                    )}
                    {documentBackUrl && (
                      <img src={documentBackUrl} alt="Reverso" className="w-1/2 h-24 object-cover rounded-lg" />
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Estos datos se leyeron de tu documento. Si algo está mal, puedes corregirlo.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">
                        NOMBRE COMPLETO
                      </label>
                      <input
                        type="text"
                        value={extractedData.full_name}
                        onChange={(e) => setExtractedData({...extractedData, full_name: e.target.value})}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">
                        RUT / NÚMERO DE DOCUMENTO
                      </label>
                      <input
                        type="text"
                        value={extractedData.rut_passport}
                        onChange={(e) => setExtractedData({...extractedData, rut_passport: e.target.value})}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          NACIONALIDAD
                        </label>
                        <input
                          type="text"
                          value={extractedData.nationality}
                          onChange={(e) => setExtractedData({...extractedData, nationality: e.target.value})}
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">
                          FECHA NACIMIENTO
                        </label>
                        <input
                          type="text"
                          value={extractedData.birth_date}
                          onChange={(e) => setExtractedData({...extractedData, birth_date: e.target.value})}
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                          placeholder="DD/MM/AAAA"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {!extractionLoading && (
              <>
                <button
                  onClick={handleConfirmExtractedData}
                  className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Datos correctos, continuar →
                </button>

                <button
                  onClick={() => { setStep(2); openScanner('document-front'); }}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition"
                >
                  🔄 Volver a escanear documento
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 5: Datos UAF */}
        {step === 5 && (
          <form onSubmit={handleUafSubmit} className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-2">Unos datos mas y listo</h2>
              <p className="text-sm text-gray-500 mb-4">
                Esto es obligatorio por ley chilena para enviar dinero. Solo se pide una vez.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Donde vives (direccion)
                  </label>
                  <input
                    type="text"
                    value={uafData.address}
                    onChange={(e) => setUafData({...uafData, address: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                    placeholder="Calle, número, depto"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Ciudad donde vives
                  </label>
                  <input
                    type="text"
                    value={uafData.city}
                    onChange={(e) => setUafData({...uafData, city: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none"
                    placeholder="Ej: Santiago"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    A que te dedicas
                  </label>
                  <select
                    value={uafData.occupation}
                    onChange={(e) => setUafData({...uafData, occupation: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none bg-white"
                    required
                  >
                    <option value="">Selecciona...</option>
                    {occupations.map(occ => (
                      <option key={occ} value={occ}>{occ}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    De donde viene el dinero que envias
                  </label>
                  <select
                    value={uafData.source_of_funds}
                    onChange={(e) => setUafData({...uafData, source_of_funds: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-green-500 focus:outline-none bg-white"
                    required
                  >
                    <option value="">Selecciona...</option>
                    {sourcesOfFunds.map(src => (
                      <option key={src} value={src}>{src}</option>
                    ))}
                  </select>
                </div>

                {/* PEP - Sí/No */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="font-semibold text-gray-800 mb-1">¿Usted es Persona Políticamente Expuesta (PEP)?</p>
                  <p className="text-xs text-gray-600 mb-3">
                    Funcionarios públicos, políticos, jueces, militares de alto rango, o familiares directos de estos.
                  </p>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_pep"
                        checked={uafData.is_pep === false}
                        onChange={() => setUafData({...uafData, is_pep: false})}
                        className="w-5 h-5 text-green-600 border-2 border-gray-300 focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-700">No</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_pep"
                        checked={uafData.is_pep === true}
                        onChange={() => setUafData({...uafData, is_pep: true})}
                        className="w-5 h-5 text-green-600 border-2 border-gray-300 focus:ring-green-500"
                      />
                      <span className="font-medium text-gray-700">Sí</span>
                    </label>
                  </div>
                </div>

                {/* Términos y Condiciones */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 text-green-600 rounded border-2 border-gray-300 focus:ring-green-500"
                    />
                    <div>
                      <p className="text-sm text-gray-800">
                        Acepto los{' '}
                        <a 
                          href="/terminos" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-600 font-bold underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Términos y Condiciones
                        </a>
                        {' '}de {BRAND.name}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Consentimiento biométrico — Ley 21.719 (fuente única) */}
                <BiometricConsentBlock
                  checked={biometricConsent}
                  onChange={setBiometricConsent}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!acceptTerms || !biometricConsent}
              className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuar, solo falta la selfie →
            </button>
          </form>
        )}

        {/* Step 7: Resultado */}
        {step === 7 && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              {loading ? (
                <>
                  <div className="w-14 h-14 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Creando tu cuenta...</h2>
                  <p className="text-gray-600">Esto toma unos segundos, no cierres la app</p>
                </>
              ) : verificationResult ? (
                <>
                  {verificationResult.status === 'approved' ? (
                    <>
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h2 className="text-xl font-bold text-green-700 mb-2">Tu cuenta esta lista</h2>
                      <p className="text-gray-600">Ya puedes enviar dinero. Redirigiendo...</p>
                    </>
                  ) : verificationResult.status === 'pending' ? (
                    <>
                      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </div>
                      <h2 className="text-xl font-bold text-yellow-700 mb-2">Cuenta creada</h2>
                      <p className="text-gray-600">
                        Estamos revisando tus documentos. Te avisaremos cuando este listo.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </div>
                      <h2 className="text-xl font-bold text-red-700 mb-2">Hubo un problema</h2>
                      <p className="text-gray-600">{verificationResult.message || 'Intenta de nuevo o contactanos por WhatsApp'}</p>
                    </>
                  )}

                  {/* Resultado oculto al cliente */}
                </>
              ) : (
                <>
                  <div className="w-14 h-14 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Procesando...</h2>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterNew;
